import { Interpolation } from '../util/Interpolation';

interface APIResponseFormat {
    start: number;
    end: number;
    total: number;
    base: number;
    quantum_start?: undefined | number;
    quantum_end?: undefined | number;
    jackpot_amount?: undefined | number;
    base_amount?: undefined | number;
}

export class VirtualCounter {
    /**
     * The endpoint this counter hits for data
     */
    _endpoint: string;

    /**
     * Last time jackpot API was hit
     */
    checkedJackpotAt = -Infinity;

    /**
     * The RequestAnimationFrame ID for clearing purposes
     */
    _rafID: number = Infinity;

    /**
     * HTML Status of Endpoint
     */
    endpointStatus: number = 0;

    /**
     * Units in seconds the counter increments
     */
    _rate: number | null = null;

    /**
     * Round trip time to server
     */
    _latency: number | null = null;

    /**
     * units in seconds the animation can move at before
     * flipping to a rolling counter instead of instant.
     * Lower than this is a rolling animation. higher
     * is instant
     */
    MIN_ANIMATED_RATE = 0.5;

    /**
     * determines based on rate whether to animate
     */
    get animated(): boolean {
        if (!this._rate) return false;
        return this._rate > this.MIN_ANIMATED_RATE;
    }

    /**
     * The time in seconds that the counter starts progressing
     * through the units
     */
    _startTime: number | null = null;

    /**
     * The time in seconds that the counter starts progressing
     * through the units
     */
    _endTime: number | null = null;

    /**
     * The starting amount for the counter. The amount the counter will be at at startTime
     */
    _startJackpot: number | null = null;

    /**
     * The ending amount for the counter. The amount the counter will be at at endTime
     */
    _endJackpot: number | null = null;

    /**
     * Returns when the jackpot session will expire
     * so VC can rehit the API
     */
    get jackpotSessionExpired(): boolean {
        // Guard to skip checks if jackpot NEVER checked
        if (this.checkedJackpotAt < 0) return true;

        // helper func to validate numbers as pos and number type
        const _isValidNumber = (num: unknown): num is number => {
            return typeof num === 'number' && !isNaN(num) && num > 0;
        };

        // Guard against null
        if (!_isValidNumber(this._endTime)) {
            console.warn(`End Time is invalid: ${this._endTime}`);
            return true;
        }
        if (!_isValidNumber(this._latency)) {
            console.warn(`Latency is invalid: ${this._latency}`);
            return true;
        }

        return Date.now() >= this._endTime - this._latency;
    }

    /**
     * Return the current state of the virtual counter
     */
    get current(): {
        current: number;
        next: number;
        progress: number;
    } {
        const ERROR_CURRENT = { current: 0, next: 0, progress: 0 };
        // guard against no api response
        if (this.endpointStatus < 200 || this.endpointStatus >= 400) {
            return ERROR_CURRENT;
        }

        if (!this._startTime || !this._endTime) {
            console.warn('Start time and/or End time undefined');
            return ERROR_CURRENT;
        }

        if (!this._startJackpot || !this._endJackpot) {
            console.warn('Start Jackpot and/or End Jackpot was undefined');
            return ERROR_CURRENT;
        }

        if (!this._rate) {
            console.warn('Rate undefined');
            return ERROR_CURRENT;
        }

        // calc time progress overall
        const time = Date.now() - this._startTime;
        const duration = this._endTime - this._startTime;
        const progress = Interpolation.Clamp(time / duration, 0, 1);

        // calc progress from one money unit to the next money unit
        // diff between float and int is progress to next char
        const currentValue = Interpolation.Lerp(this._startJackpot, this._endJackpot, progress);
        const currentInt = Math.floor(currentValue);
        const currentProgress = currentValue - currentInt;

        // potential flaw to always assume next int is +1
        const nextInt = currentInt + 1;

        // choose easing func based on how fast were going.
        const easingFunc = this._rate > this.MIN_ANIMATED_RATE ? Interpolation.EasInOutInstant : Interpolation.EaseInOutQuart;

        // build state obj
        const state = {
            current: currentInt,
            next: nextInt,
            progress: easingFunc(currentProgress),
        };

        // return!
        return state;
    }

    constructor(endpoint: string) {
        this._endpoint = endpoint;
        this.getJackpot();
        this.start();
    }

    /**
     * Kicks off the raf
     */
    start() {
        this.update();
    }

    /**
     * stops the raf
     */
    stop() {
        window.clearTimeout(this._rafID);
    }

    /**
     * One Timestep
     */
    // Todo (dt?)
    update() {
        if (this.jackpotSessionExpired) {
            this.getJackpot();
        }

        this._rafID = requestAnimationFrame(() => {
            this.update();
        });
    }

    /**
     * Hit the API, and initialize the counter's state
     */
    getJackpot() {
        const requestTimestamp = Date.now(); // 1000;

        // does last checkedjackpot exist
        // has it been too soon since last check
        const tooSoon = requestTimestamp - this.checkedJackpotAt < 100;
        if (this.checkedJackpotAt && tooSoon) return;

        this.checkedJackpotAt = requestTimestamp;

        this._apiRequest();
    }

    /**
     * Make API request, passing data to handleResponse()
     */
    // TODO: Guard for undefined endpoint
    _apiRequest() {
        const request = new XMLHttpRequest();

        request.open('GET', this._endpoint, true);

        request.onload = () => {
            const data = JSON.parse(request.response);
            this.endpointStatus = request.status;

            this._handleResponse(data, this.endpointStatus);
        };
        request.send();
    }

    /**
     * Heavy lifting function taking API data and initializing
     * counter's internal state
     */
    _handleResponse = (data: APIResponseFormat, status: number) => {
        const standardize = (first: undefined | number, second: undefined | number) => {
            return first != undefined ? first : second != undefined ? second : 0;
        };

        const nowInMillis = Date.now(); // 1000;

        // guard against bad statuses
        if (!(status >= 200 && status < 400)) return;

        // standardize based on which API is hit
        data.start = standardize(data.start, data.quantum_start);
        data.end = standardize(data.end, data.quantum_end);
        data.total = standardize(data.total, data.jackpot_amount);
        data.base = standardize(data.base, data.base_amount);

        // adapt for server units
        // / 1000 for sever seconds
        // / 100 for pennies to dollars
        data.start = data.start * 1000;
        data.end = data.end * 1000;

        // track some time stuff
        this._latency = nowInMillis - this.checkedJackpotAt;

        // console.log(`${this._endpoint} will expire in  ${data.end - data.start - this._latency}ms`)
        // setTimeout(() => {
        //   console.log(`JACKPOT DATA HAS EXPIRED ${this._endpoint}`);
        // }, data.end - data.start - this._latency);

        // validate that code
        this.validate(data, `${data.end}`.length);

        this._startTime = nowInMillis;
        this._endTime = Math.floor(this._startTime) + Math.max(data.end - data.start, 100);

        const progress = (nowInMillis - this.checkedJackpotAt) / (data.end - data.start);
        const realBaseValue = Interpolation.Lerp(data.base, data.total, progress);
        const correctedJackpotAmount = Math.floor(Interpolation.Clamp(realBaseValue, 0, data.total));

        this._rate = data.total == data.base ? 0 : ((data.total - data.base) / (data.end - data.start)) * 1000;

        this._startJackpot = correctedJackpotAmount;
        this._endJackpot = data.total;
    };

    /**
     * Perform error checking, mostly related to digit length vs
     * available counter spaces
     */
    // TODO: zod opportunity
    validate(data: APIResponseFormat, digits: number) {
        // TODO: , discuss totalString being derrived from digits,
        // meaning error checking may not be robust
        const totalString = data.total.toString().padStart(digits, '0');
        const baseString = data.base.toString().padStart(digits, '0');

        if (data.start > data.end) {
            console.error(`Error ${this._endpoint}: end time cannot be before start time`);
        }

        if (data.base > data.total) {
            console.error(`Error ${this._endpoint}: base amount cannot be greater than total amount`);
        }

        if (totalString.length > digits) {
            console.error(`Error ${this._endpoint}: total amount is too large to fit within the ${digits} digits limit. Total string is: ${totalString}`);
        }

        if (baseString.length > digits) {
            console.error(`Error ${this._endpoint}: base amount is too large to fit within the ${digits} digits limit. Base string is: ${baseString}`);
        }
    }
}
